import { Alert, HazardType, LatLng } from "../types";
import { supabase } from "./supabaseClient";

// Helper to get HQ images for hazards
export const getHazardImage = (type: HazardType): string => {
  switch (type) {
    case HazardType.EARTHQUAKE: return 'https://images.unsplash.com/photo-1594750018612-5e485413f01b?w=800&q=80';
    case HazardType.FIRE: return 'https://images.unsplash.com/photo-1612133458645-560fae7403d7?w=800&q=80';
    case HazardType.FLOOD: return 'https://images.unsplash.com/photo-1547623645-a75e2ec3a450?w=800&q=80';
    case HazardType.TSUNAMI: return 'https://images.unsplash.com/photo-1518182181559-07521d95ee0d?w=800&q=80';
    case HazardType.TORNADO: return 'https://images.unsplash.com/photo-1527482797697-8795b05a13fe?w=800&q=80';
    case HazardType.BIOHAZARD: return 'https://images.unsplash.com/photo-1605289982774-9a6fef564df8?w=800&q=80';
    case HazardType.CRASH: return 'https://images.unsplash.com/photo-1566835105370-9831a298816c?w=800&q=80';
    case HazardType.TRAFFIC: return 'https://images.unsplash.com/photo-1455459303976-71343cd0929b?w=800&q=80';
    case HazardType.WEATHER: return 'https://images.unsplash.com/photo-1562207573-71860c55f211?w=800&q=80';
    case HazardType.CIVIL: return 'https://images.unsplash.com/photo-1558066862-2337d1d28373?w=800&q=80';
    default: return 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80'; // Genetic Tech/Map bg
  }
};

// USGS Earthquake Feed - Multiple feeds for comprehensive coverage
const USGS_API_ALL = "https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/2.5_week.geojson";
const USGS_API_SIGNIFICANT = "https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/significant_month.geojson";

// GDACS API (Global Disaster Alert and Coordination System) - RSS Feed (needs parsing)
const GDACS_FEED = "https://www.gdacs.org/xml/rss.xml";

// EMSC API (Euro-Med Seismological Centre) - GeoJSON
const EMSC_API = "https://www.seismicportal.eu/fdsnws/event/1/query?format=json&limit=50&minmag=3";

// Fetch real global hazards from multiple sources
// Fetch real global hazards from multiple sources
export const fetchRealHazards = async (userLoc: LatLng, forceRefresh = false): Promise<Alert[]> => {


  // STATIC HISTORIC DATA - Always preserved
  const historicMorocco: Alert[] = [
    {
      id: '1001',
      type: HazardType.EARTHQUAKE,
      severity: 'low',
      location: 'Al Haouz, High Atlas',
      coordinates: { lat: 31.11, lng: -8.44 },
      timestamp: new Date('2023-09-08').toISOString(),
      time: 'HISTORIC (2023)',
      distance: 'Local',
      isOfficial: true,
      description: 'ARCHIVE: Historic Magnitude 6.8 Earthquake (2023). Reference data only.',
      advice: ['Historic event marker', 'No active threat', 'Educational data'],
      source: 'USGS / Archive',
      truthScore: 100,
      magnitude: 6.8,
      depth: 18,
      imageUrl: getHazardImage(HazardType.EARTHQUAKE)
    }
  ];

  const cacheBuster = forceRefresh ? `?t=${Date.now()}` : '';

  try {
    // PARALLEL FETCHING from Multiple Sources
    const [usgsRes, emscRes, gdacsRes] = await Promise.all([
      // 1. USGS (US/Global)
      fetch(USGS_API_ALL + cacheBuster).catch(() => null),
      // 2. EMSC (Euro-Med / Morocco - Critical for user)
      fetch(EMSC_API).catch(() => null),
      // 3. GDACS (Floods, Cyclones, etc.) - Using CORS Proxy to bypass browser restrictions
      fetch(`https://api.allorigins.win/raw?url=${encodeURIComponent(GDACS_FEED)}`).catch(() => null)
    ]);

    const alerts: Alert[] = [];
    const seenIds = new Set<string>();

    // --- PROCESS EMSC (Priority for Morocco) ---
    if (emscRes?.ok) {
      try {
        const emscData = await emscRes.json();
        emscData.features?.forEach((feature: any) => {
          const id = `emsc-${feature.id || feature.properties?.unid || Math.random()}`;
          if (seenIds.has(id)) return;
          seenIds.add(id);

          const alert = parseEMSCFeature(feature, userLoc);
          if (alert) alerts.push(alert);
        });

      } catch (e) {
        console.warn("Failed to parse EMSC", e);
      }
    }

    // --- PROCESS GDACS (General Disasters) ---
    if (gdacsRes?.ok) {
      try {
        const text = await gdacsRes.text();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(text, "text/xml");
        const items = xmlDoc.getElementsByTagName("item");

        for (let i = 0; i < items.length; i++) {
          const alert = parseGDACSItem(items[i], userLoc);
          if (alert && !seenIds.has(alert.id)) {
            seenIds.add(alert.id);
            alerts.push(alert);
          }
        }

      } catch (e) {
        console.warn("Failed to parse GDACS", e);
      }
    }

    // --- PROCESS USGS (Fallback/Global) ---
    if (usgsRes?.ok) {
      try {
        const usgsData = await usgsRes.json();
        usgsData.features?.forEach((feature: any) => {
          if (seenIds.has(feature.id)) return;
          seenIds.add(feature.id);
          const alert = parseUSGSFeature(feature, userLoc, false);
          if (alert) alerts.push(alert);
        });
      } catch (e) { console.warn("USGS Parse fail", e); }
    }

    // --- FILTERING & REFINEMENT ---
    const now = Date.now();
    const refinedAlerts: Alert[] = [];

    alerts.forEach(a => {
      const time = new Date(a.timestamp).getTime();
      const ageHours = (now - time) / (1000 * 3600);

      // Keep everything relevant (recent 7 days)
      if (ageHours < 168) {
        refinedAlerts.push(a);
      }
    });

    // 4. FETCH REAL-TIME SUPABASE ALERTS (AI Agent & User Reports)
    try {
      const { data: supaHazards } = await supabase
        .from('hazards')
        .select('*')
        .eq('is_active', true); // Only get active hazards

      if (supaHazards) {
        supaHazards.forEach((hazard: any) => {
          // Parse coordinates from string "(lat,lng)" or JSON
          let lat = 0, lng = 0;
          if (typeof hazard.coordinates === 'string') {
            const parts = hazard.coordinates.replace(/[()]/g, '').split(',');
            lat = parseFloat(parts[0]);
            lng = parseFloat(parts[1]);
          } else if (hazard.coordinates?.lat) {
            lat = hazard.coordinates.lat;
            lng = hazard.coordinates.lng;
          }

          // Avoid dupes if same as API
          const existingId = combinedAlerts.find(a =>
            Math.abs(a.coordinates.lat - lat) < 0.01 &&
            Math.abs(a.coordinates.lng - lng) < 0.01 &&
            a.type === hazard.type
          );

          if (!existingId) {
            refinedAlerts.push({
              id: `supa-${hazard.id}`,
              type: hazard.type,
              severity: hazard.severity,
              location: hazard.location,
              coordinates: { lat, lng },
              timestamp: hazard.created_at || new Date().toISOString(),
              time: getTimeAgo(new Date(hazard.created_at || Date.now())),
              distance: `${Math.round(getDistanceFromLatLonInKm(userLoc.lat, userLoc.lng, lat, lng))}km`,
              isOfficial: hazard.source !== 'USER_REPORT', // User reports might not be official yet
              description: hazard.description,
              advice: hazard.metadata?.action_step ? [hazard.metadata.action_step] : ['Monitor local situation'],
              source: hazard.source === 'AI_AGENT' ? 'Protectly AI' : 'Community Report',
              truthScore: hazard.source === 'AI_AGENT' ? 95 : 80,
              imageUrl: getHazardImage(hazard.type)
            } as Alert);
          }
        });

      }
    } catch (err) {
      console.warn('Failed to fetch Supabase hazards', err);
    }

    // Combine real API data with historic data
    const combinedAlerts = [...historicMorocco, ...refinedAlerts];

    // Return ALL relevant hazards
    return combinedAlerts;

  } catch (error) {
    console.error("❌ Failed to fetch hazard data", error);
    // On ERROR, we return historic data
    return historicMorocco;
  }
};

/**
 * Subscribe to real-time hazard updates
 */
export const subscribeToRealtimeHazards = (
  callback: (payload: any) => void
) => {
  return supabase
    .channel('public:hazards')
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'hazards' }, (payload) => {
      console.log('⚡ Real-time Hazard Alert:', payload);
      callback(payload.new);
    })
    .subscribe();
};

// Parse USGS earthquake feature into our Alert format
function parseUSGSFeature(feature: any, userLoc: LatLng, isSignificant: boolean): Alert | null {
  try {
    const [lng, lat, depth] = feature.geometry.coordinates;
    const mag = feature.properties.mag;
    const place = feature.properties.place;
    const time = new Date(feature.properties.time);
    const tsunami = feature.properties.tsunami;
    const felt = feature.properties.felt;

    // Skip if coordinates are invalid
    if (lat === undefined || lng === undefined || isNaN(lat) || isNaN(lng)) {
      return null;
    }

    // Calculate distance from user
    const distKm = getDistanceFromLatLonInKm(userLoc.lat, userLoc.lng, lat, lng);

    // Determine severity based on magnitude
    let severity: 'critical' | 'high' | 'medium' | 'low' = 'low';
    if (mag >= 7 || isSignificant) {
      severity = 'critical';
    } else if (mag >= 6) {
      severity = 'high';
    } else if (mag >= 4.5) {
      severity = 'medium';
    }

    // Truth score based on data source and confirmation
    const truthScore = isSignificant ? 98 : (felt && felt > 100 ? 95 : 85);
    const timeAgo = getTimeAgo(time);

    // Safety Directive Logic (New Accuracy Layer)
    let safetyDirective = "Monitor local alerts.";
    if (distKm < 50) {
      if (severity === 'critical') safetyDirective = "IMMEDIATE EVACUATION ADVISED. SEEK HIGH GROUND OR COVER.";
      else if (severity === 'high') safetyDirective = "PREPARE FOR POTENTIAL EVACUATION. PACK EMERGENCY BAG.";
      else if (severity === 'medium') safetyDirective = "Stay alert. Secure loose objects.";
    } else if (distKm < 200) {
      if (severity === 'critical') safetyDirective = "High alert. Be ready for aftershocks.";
    }

    return {
      id: feature.id || `eq-${Date.now()}-${Math.random()}`,
      type: HazardType.EARTHQUAKE,
      severity,
      location: place || 'Unknown Location',
      coordinates: { lat, lng },
      timestamp: time.toISOString(),
      time: timeAgo,
      distance: distKm < 100 ? `${Math.round(distKm)}km` : distKm < 1000 ? `${Math.round(distKm)}km` : `${(distKm / 1000).toFixed(1)}k km`,
      isOfficial: true,
      description: `Magnitude ${mag?.toFixed(1) || '?'} earthquake at ${depth?.toFixed(0) || '?'}km depth. ${safetyDirective} ${tsunami ? ' ⚠️ TSUNAMI WARNING' : ''}${felt ? ` Felt by ${felt} people.` : ''}`,
      advice: [
        tsunami ? "MOVE TO HIGH GROUND IMMEDIATELY." : "DROP, COVER, AND HOLD ON.",
        "Stay away from windows.",
        "Expect aftershocks.",
        safetyDirective // Add directive to advice list
      ],
      source: 'USGS',
      truthScore,
      magnitude: mag,
      depth,
      imageUrl: getHazardImage(HazardType.EARTHQUAKE)
    } as Alert;
  } catch (e) {
    console.error('Error parsing USGS feature:', e);
    return null;
  }
}

// --- PARSERS FOR NEW FEEDS ---

function parseEMSCFeature(feature: any, userLoc: LatLng): Alert | null {
  try {
    const props = feature.properties;
    const geo = feature.geometry;
    if (!props || !geo) return null;

    const [lng, lat] = geo.coordinates; // EMSC GeoJSON is usually [lng, lat, depth]
    const depth = geo.coordinates[2] || 0;

    const mag = props.mag;
    const region = props.flynn_region || props.region || "Unknown Region";
    const time = new Date(props.time);

    const distKm = getDistanceFromLatLonInKm(userLoc.lat, userLoc.lng, lat, lng);

    // Severity Logic
    let severity: 'low' | 'medium' | 'high' | 'critical' = 'low';
    if (mag > 6) severity = 'critical';
    else if (mag > 5) severity = 'high';
    else if (mag > 4) severity = 'medium';

    return {
      id: `emsc-${props.source_id || Math.random()}`,
      type: HazardType.EARTHQUAKE,
      severity,
      location: region,
      coordinates: { lat, lng },
      timestamp: time.toISOString(),
      time: getTimeAgo(time),
      distance: `${Math.round(distKm)}km`,
      isOfficial: true,
      description: `M${mag} Earthquake detected by EMSC. Depth: ${depth}km.`,
      advice: ['Drop, Cover, Hold on', 'Be aware of aftershocks'],
      source: 'EMSC (Euro-Med)',
      truthScore: 100,
      magnitude: mag,
      depth,
      imageUrl: getHazardImage(HazardType.EARTHQUAKE)
    } as Alert;

  } catch (e) { console.warn("EMSC Parse Error", e); return null; }
}

function parseGDACSItem(item: Element, userLoc: LatLng): Alert | null {
  try {
    // GDACS RSS items have <title>, <description>, <geo:lat>, <geo:long>, <gdacs:eventtype>, <gdacs:alertlevel>
    const title = item.getElementsByTagName('title')[0]?.textContent || '';
    const latStr = item.getElementsByTagName('geo:lat')[0]?.textContent;
    const lngStr = item.getElementsByTagName('geo:long')[0]?.textContent;
    const typeStr = item.getElementsByTagName('gdacs:eventtype')[0]?.textContent; // EQ, TC, FL, VO, DR
    const levelStr = item.getElementsByTagName('gdacs:alertlevel')[0]?.textContent; // Green, Orange, Red
    const dateStr = item.getElementsByTagName('pubDate')[0]?.textContent;

    if (!latStr || !lngStr) return null;

    const lat = parseFloat(latStr);
    const lng = parseFloat(lngStr);
    const distKm = getDistanceFromLatLonInKm(userLoc.lat, userLoc.lng, lat, lng);

    // Map Types
    let type = HazardType.CIVIL;
    if (typeStr === 'EQ') type = HazardType.EARTHQUAKE;
    if (typeStr === 'TC') type = HazardType.TORNADO; // Tropical Cyclone
    if (typeStr === 'FL') type = HazardType.FLOOD;
    if (typeStr === 'VO') type = HazardType.FIRE; // Volcano (Use Fire icon for now or Generic)
    if (typeStr === 'DR') type = HazardType.WEATHER; // Drought

    // Map Severity
    let severity: 'low' | 'medium' | 'high' | 'critical' = 'low';
    if (levelStr === 'Red') severity = 'critical';
    else if (levelStr === 'Orange') severity = 'high';
    else if (levelStr === 'Green') severity = 'low';

    return {
      id: `gdacs-${Date.now()}-${Math.random()}`, // RSS doesn't always have simple ID
      type,
      severity,
      location: title.split(':')[1]?.trim() || title,
      coordinates: { lat, lng },
      timestamp: dateStr ? new Date(dateStr).toISOString() : new Date().toISOString(),
      time: dateStr ? getTimeAgo(new Date(dateStr)) : 'Results Just In',
      distance: `${Math.round(distKm)}km`,
      isOfficial: true,
      description: `GDACS Alert: ${title}. ${item.getElementsByTagName('description')[0]?.textContent || ''}`,
      advice: ['Monitor local media', 'Follow emergency services'],
      source: 'UN GDACS',
      truthScore: 100,
      imageUrl: getHazardImage(type)
    } as Alert;

  } catch (e) { console.warn("GDACS Parse Error", e); return null; }
}

// Fallback test hazards for Morocco region
function getTestHazards(userLoc: LatLng): Alert[] {


  // Historic and simulated hazards for Morocco and nearby regions
  return [
    {
      id: 'test-morocco-1',
      type: HazardType.EARTHQUAKE,
      severity: 'critical' as const,
      location: 'Al Haouz, Morocco',
      coordinates: { lat: 31.0671, lng: -8.4286 },
      timestamp: new Date('2023-09-08T22:11:00Z').toISOString(),
      time: 'Sep 2023',
      distance: '100km',
      isOfficial: true,
      description: 'Magnitude 6.8 earthquake - Historic event that affected the High Atlas region.',
      advice: ['Historic reference event', 'Major damage was reported in surrounding areas'],
      source: 'USGS Archive',
      truthScore: 100,
      magnitude: 6.8,
      depth: 18,
      imageUrl: getHazardImage(HazardType.EARTHQUAKE)
    },
    {
      id: 'test-algeria-1',
      type: HazardType.EARTHQUAKE,
      severity: 'medium' as const,
      location: 'Near Oran, Algeria',
      coordinates: { lat: 35.6987, lng: -0.6349 },
      timestamp: new Date(Date.now() - 86400000 * 5).toISOString(),
      time: '5 days ago',
      distance: '300km',
      isOfficial: true,
      description: 'Magnitude 4.2 earthquake in northwestern Algeria.',
      advice: ['Monitor local news', 'Standard safety precautions apply'],
      source: 'USGS',
      truthScore: 88,
      magnitude: 4.2,
      depth: 10,
      imageUrl: getHazardImage(HazardType.EARTHQUAKE)
    },
    {
      id: 'test-spain-1',
      type: HazardType.EARTHQUAKE,
      severity: 'low' as const,
      location: 'Southern Spain',
      coordinates: { lat: 36.7213, lng: -4.4214 },
      timestamp: new Date(Date.now() - 86400000 * 2).toISOString(),
      time: '2 days ago',
      distance: '200km',
      isOfficial: true,
      description: 'Minor seismic activity detected in Andalusia region.',
      advice: ['No action required', 'Normal seismic activity for the region'],
      source: 'USGS',
      truthScore: 90,
      magnitude: 3.1,
      depth: 8,
      imageUrl: getHazardImage(HazardType.EARTHQUAKE)
    },
    {
      id: 'test-atlantic-1',
      type: HazardType.EARTHQUAKE,
      severity: 'medium' as const,
      location: 'Atlantic Ocean, West of Casablanca',
      coordinates: { lat: 33.5731, lng: -10.1234 },
      timestamp: new Date(Date.now() - 86400000 * 1).toISOString(),
      time: '1 day ago',
      distance: '150km',
      isOfficial: true,
      description: 'Magnitude 4.5 undersea earthquake detected in the Atlantic.',
      advice: ['No tsunami risk', 'Normal activity for oceanic region'],
      source: 'USGS',
      truthScore: 92,
      magnitude: 4.5,
      depth: 25,
      imageUrl: getHazardImage(HazardType.EARTHQUAKE)
    }
  ];
}

// Get human-readable time ago
function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return `${Math.floor(diffDays / 7)}w ago`;
}

// Haversine Formula
function getDistanceFromLatLonInKm(lat1: number, lon1: number, lat2: number, lon2: number) {
  var R = 6371; // Radius of the earth in km
  var dLat = deg2rad(lat2 - lat1);
  var dLon = deg2rad(lon2 - lon1);
  var a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  var d = R * c; // Distance in km
  return d;
}

function deg2rad(deg: number) {
  return deg * (Math.PI / 180);
}
